/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { AttributeValueModel } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

interface OwnProps {
    maximumNumberAttributeTypes: number;
    extendedStyles?: ProductAttributesStyles;
}

type Props = OwnProps & HasProduct;

export interface ProductAttributesStyles {
    allAttributesWrapper?: InjectableCss;
    attributeWrapper?: InjectableCss;
    attributeTypeWrapper?: InjectableCss;
    attributeValueWrapper?: InjectableCss;
    attributeTypeText?: TypographyPresentationProps;
    attributeValueText?: TypographyPresentationProps;
}

export const productAttributesStyles: ProductAttributesStyles = {};

const ProductAttributes: FC<Props> = ({ product, maximumNumberAttributeTypes, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(productAttributesStyles, extendedStyles));
    const { attributeTypes } = product;

    const maximum = Math.min(maximumNumberAttributeTypes, 100);
    const limitedAttributeTypes = attributeTypes?.slice(0, maximum);

    const displayAttributeValues = (attributeValues: AttributeValueModel[] | null) => {
        return (
            <Typography {...styles.attributeValueText} title={attributeValues?.map(a => a.valueDisplay).join(" | ")}>
                {(attributeValues ?? []).map(a => a.valueDisplay).join(" | ")}
            </Typography>
        );
    };

    return (
        <StyledWrapper {...styles.allAttributesWrapper} className="attrs-cont">
            {limitedAttributeTypes &&
                limitedAttributeTypes.map(attributeType => (
                    <StyledWrapper
                        key={attributeType.id.toString()}
                        {...styles.attributeWrapper}
                        className="single-attr-cont"
                    >
                        <StyledWrapper className="attr-label" {...styles.attributeTypeWrapper}>
                            <Typography
                                {...styles.attributeTypeText}
                                data-test-selector={`attributeType${attributeType.id}`}
                                title={attributeType.label || attributeType.name}
                            >
                                {attributeType.label || attributeType.name}:
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper
                            className="attr-value-cont"
                            {...styles.attributeValueWrapper}
                            data-test-selector={`attributeValuesFor${attributeType.id}`}
                            translate="no"
                        >
                            {displayAttributeValues(attributeType.attributeValues)}
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
        </StyledWrapper>
    );
};

export default withProduct(ProductAttributes);
